import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { apiService } from '@/services/api';
import { UserUpdate } from '@/types/api';
import Card from '@/components/atoms/Card';
import Button from '@/components/atoms/Button';
import { FormField } from '@/components/molecules/FormField';
import PasswordStrengthIndicator from '@/components/atoms/PasswordStrengthIndicator';
import './Profile.scss';

const Profile: React.FC = () => {
  const { t } = useTranslation();
  const { user, isAuthenticated, refreshUser } = useAuth();
  
  const [isEditing, setIsEditing] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  const [formData, setFormData] = useState<UserUpdate>({
    email: '',
    username: '',
    first_name: '',
    last_name: '',
    phone: '',
    address: '',
    date_of_birth: '',
    gender: null
  });

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const [passwordFormData, setPasswordFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [passwordErrors, setPasswordErrors] = useState<Record<string, string>>({});

  // Helper function to format date for HTML date input (yyyy-MM-dd)
  const formatDateForInput = (dateString: string | null | undefined): string => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toISOString().split('T')[0]; // Returns yyyy-MM-dd format
    } catch (error) {
      console.warn('Invalid date format:', dateString);
      return '';
    }
  };

  // Load user data when component mounts
  useEffect(() => {
    if (user) {
      setFormData({
        email: user.email || '',
        username: user.username || '',
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone: user.phone || '',
        address: user.address || '',
        date_of_birth: formatDateForInput(user.date_of_birth),
        gender: user.gender || null
      });
    }
  }, [user]);

  const handleInputChange = (field: keyof UserUpdate, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handlePasswordInputChange = (field: keyof typeof passwordFormData, value: string) => {
    setPasswordFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (passwordErrors[field]) {
      setPasswordErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.email) {
      errors.email = t('errors.validation.required');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = t('errors.validation.email');
    }

    if (!formData.username) {
      errors.username = t('errors.validation.required');
    }

    if (!formData.first_name) {
      errors.first_name = t('errors.validation.required');
    }

    if (!formData.last_name) {
      errors.last_name = t('errors.validation.required');
    }

    if (formData.phone && !/^[\+]?[1-9][\d\s\-\(\)]{0,20}$/.test(formData.phone)) {
      errors.phone = t('errors.validation.phone');
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validatePasswordForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!passwordFormData.currentPassword) {
      errors.currentPassword = t('errors.validation.required');
    }

    if (!passwordFormData.newPassword) {
      errors.newPassword = t('errors.validation.required');
    } else if (passwordFormData.newPassword.length < 6) {
      errors.newPassword = t('errors.validation.password');
    }

    if (!passwordFormData.confirmPassword) {
      errors.confirmPassword = t('errors.validation.required');
    } else if (passwordFormData.newPassword !== passwordFormData.confirmPassword) {
      errors.confirmPassword = t('errors.validation.passwordMatch');
    }

    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      setIsSubmitting(true);
      setError(null);
      setSuccess(null);

      if (!user?.id) {
        throw new Error('User ID not found');
      }

      const updatedUser = await apiService.updateUser(user.id, formData);
      
      setSuccess(t('profile.updateSuccess'));
      setIsEditing(false);
      
      // Refresh user data in context to reflect changes
      await refreshUser();
      
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || t('errors.general'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!validatePasswordForm()) return;

    try {
      setIsSubmitting(true);
      setError(null);
      setSuccess(null);

      // Change password using the API
      await apiService.changePassword(
        passwordFormData.currentPassword,
        passwordFormData.newPassword
      );

      setSuccess(t('profile.passwordChangeSuccess'));
      setIsChangingPassword(false);
      
      // Reset password form
      setPasswordFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setPasswordErrors({});
      
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || t('errors.general');
      
      // Check if it's a password-related error
      if (errorMessage.includes('contraseña') || errorMessage.includes('password')) {
        setPasswordErrors(prev => ({ ...prev, currentPassword: errorMessage }));
      } else {
        setError(errorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    // Reset form data to original user data
    if (user) {
      setFormData({
        email: user.email || '',
        username: user.username || '',
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone: user.phone || '',
        address: user.address || '',
        date_of_birth: formatDateForInput(user.date_of_birth),
        gender: user.gender || null
      });
    }
    setFormErrors({});
    setError(null);
    setSuccess(null);
    setIsEditing(false);
  };

  const handlePasswordCancel = () => {
    setPasswordFormData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setPasswordErrors({});
    setIsChangingPassword(false);
  };

  if (!isAuthenticated) {
    return (
      <div className="profile">
        <div className="container">
          <Card variant="elevated" className="profile__card">
            <div className="profile__error">
              <p>{t('profile.notAuthenticated')}</p>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="profile">
        <div className="container">
          <Card variant="elevated" className="profile__card">
            <div className="profile__loading">
              <p>{t('common.loading')}</p>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="profile">
      <div className="container">
        <Card variant="elevated" className="profile__card">
          <div className="profile__header">
            <h1 className="profile__title">{t('profile.title')}</h1>
            {!isEditing && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => setIsEditing(true)}
              >
                {t('profile.edit')}
              </Button>
            )}
          </div>

          {error && (
            <div className="profile__error">
              {error}
            </div>
          )}

          {success && (
            <div className="profile__success">
              {success}
            </div>
          )}

          {isEditing ? (
            <>
              <form onSubmit={handleSubmit} className="profile__form">
                <div className="profile__form-section">
                  <h3 className="profile__section-title">{t('profile.personalInfo')}</h3>
                
                <div className="profile__form-row">
                  <FormField
                    type="text"
                    name="firstName"
                    label={t('auth.register.firstName')}
                    error={formErrors.first_name}
                    required
                    value={formData.first_name || ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => 
                      handleInputChange('first_name', e.target.value)
                    }
                    placeholder={t('auth.register.firstName')}
                    disabled={isSubmitting}
                  />

                  <FormField
                    type="text"
                    name="lastName"
                    label={t('auth.register.lastName')}
                    error={formErrors.last_name}
                    required
                    value={formData.last_name || ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => 
                      handleInputChange('last_name', e.target.value)
                    }
                    placeholder={t('auth.register.lastName')}
                    disabled={isSubmitting}
                  />
                </div>

                <FormField
                  type="email"
                  name="email"
                  label={t('auth.register.email')}
                  error={formErrors.email}
                  required
                  value={formData.email || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => 
                    handleInputChange('email', e.target.value)
                  }
                  placeholder={t('auth.register.emailPlaceholder')}
                  disabled={isSubmitting}
                />

                <FormField
                  type="tel"
                  name="phone"
                  label={t('profile.phone')}
                  error={formErrors.phone}
                  value={formData.phone || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => 
                    handleInputChange('phone', e.target.value)
                  }
                  placeholder={t('profile.phone')}
                  disabled={isSubmitting}
                />

                <FormField
                  type="date"
                  name="dateOfBirth"
                  label={t('profile.dateOfBirth')}
                  value={formData.date_of_birth || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => 
                    handleInputChange('date_of_birth', e.target.value)
                  }
                  disabled={isSubmitting}
                />

                <FormField
                  type="select"
                  name="gender"
                  label={t('profile.gender')}
                  value={formData.gender || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => 
                    handleInputChange('gender', e.target.value)
                  }
                  disabled={isSubmitting}
                >
                  <option value="">{t('profile.selectGender')}</option>
                  <option value="man">{t('profile.genderMan')}</option>
                  <option value="woman">{t('profile.genderWoman')}</option>
                  <option value="nonbinary">{t('profile.genderNonbinary')}</option>
                </FormField>
              </div>

              <div className="profile__form-section">
                <h3 className="profile__section-title">{t('profile.address')}</h3>
                
                <FormField
                  type="textarea"
                  name="address"
                  label={t('profile.address')}
                  value={formData.address || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => 
                    handleInputChange('address', e.target.value)
                  }
                  placeholder={t('profile.addressPlaceholder')}
                  disabled={isSubmitting}
                />
              </div>

              <div className="profile__form-actions">
                <Button
                  type="submit"
                  variant="primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? t('common.loading') : t('profile.save')}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleCancel}
                  disabled={isSubmitting}
                >
                  {t('common.cancel')}
                </Button>
              </div>
            </form>

            {/* Password Change Section - Outside main form to prevent conflicts */}
            <div className="profile__form-section">
              <h3 className="profile__section-title">{t('profile.changePassword')}</h3>
              
              {!isChangingPassword ? (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setIsChangingPassword(true)}
                  disabled={isSubmitting}
                >
                  {t('profile.changePassword')}
                </Button>
              ) : (
                <form onSubmit={handlePasswordSubmit} className="profile__password-form">
                  <FormField
                    type="password"
                    name="currentPassword"
                    label={t('profile.currentPassword')}
                    error={passwordErrors.currentPassword}
                    required
                    value={passwordFormData.currentPassword}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => 
                      handlePasswordInputChange('currentPassword', e.target.value)
                    }
                    placeholder={t('profile.currentPassword')}
                    disabled={isSubmitting}
                  />

                  <FormField
                    type="password"
                    name="newPassword"
                    label={t('profile.newPassword')}
                    error={passwordErrors.newPassword}
                    required
                    value={passwordFormData.newPassword}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => 
                      handlePasswordInputChange('newPassword', e.target.value)
                    }
                    placeholder={t('profile.newPassword')}
                    disabled={isSubmitting}
                  />

                  <PasswordStrengthIndicator 
                    password={passwordFormData.newPassword}
                    className="profile__password-strength"
                  />

                  <FormField
                    type="password"
                    name="confirmPassword"
                    label={t('profile.confirmPassword')}
                    error={passwordErrors.confirmPassword}
                    required
                    value={passwordFormData.confirmPassword}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => 
                      handlePasswordInputChange('confirmPassword', e.target.value)
                    }
                    placeholder={t('profile.confirmPassword')}
                    disabled={isSubmitting}
                  />

                  <div className="profile__password-actions">
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? t('common.loading') : t('profile.savePassword')}
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={handlePasswordCancel}
                      disabled={isSubmitting}
                    >
                      {t('common.cancel')}
                    </Button>
                  </div>
                </form>
              )}
            </div>
          </>
          ) : (
            <div className="profile__info">
              <div className="profile__info-section">
                <h3 className="profile__section-title">{t('profile.personalInfo')}</h3>
                
                <div className="profile__info-grid">
                  <div className="profile__info-item">
                    <label className="profile__info-label">{t('auth.register.firstName')}</label>
                    <p className="profile__info-value">{user.first_name || t('profile.notProvided')}</p>
                  </div>

                  <div className="profile__info-item">
                    <label className="profile__info-label">{t('auth.register.lastName')}</label>
                    <p className="profile__info-value">{user.last_name || t('profile.notProvided')}</p>
                  </div>

                  <div className="profile__info-item">
                    <label className="profile__info-label">{t('auth.register.email')}</label>
                    <p className="profile__info-value">{user.email}</p>
                  </div>                  

                  <div className="profile__info-item">
                    <label className="profile__info-label">{t('profile.phone')}</label>
                    <p className="profile__info-value">{user.phone || t('profile.notProvided')}</p>
                  </div>

                  <div className="profile__info-item">
                    <label className="profile__info-label">{t('profile.dateOfBirth')}</label>
                    <p className="profile__info-value">
                      {user.date_of_birth ? new Date(user.date_of_birth).toLocaleDateString() : t('profile.notProvided')}
                    </p>
                  </div>

                  <div className="profile__info-item">
                    <label className="profile__info-label">{t('profile.gender')}</label>
                    <p className="profile__info-value">
                      {user.gender ? t(`profile.gender${user.gender.charAt(0).toUpperCase() + user.gender.slice(1)}`) : t('profile.notProvided')}
                    </p>
                  </div>

                  
                </div>
              </div>

              {user.address && (
                <div className="profile__info-section">
                  <h3 className="profile__section-title">{t('profile.address')}</h3>
                  <div className="profile__info-item">
                    <p className="profile__info-value">{user.address}</p>
                  </div>
                </div>
              )}

              <div className="profile__info-section">
                <h3 className="profile__section-title">{t('profile.accountInfo')}</h3>
                
                <div className="profile__info-grid">
                  <div className="profile__info-item">
                    <label className="profile__info-label">{t('auth.register.username')}</label>
                    <p className="profile__info-value">{user.username}</p>
                  </div>

                  <div className="profile__info-item">
                    <label className="profile__info-label">{t('profile.memberSince')}</label>
                    <p className="profile__info-value">
                      {new Date(user.created_at).toLocaleDateString()}
                    </p>
                  </div>

                  <div className="profile__info-item">
                    <label className="profile__info-label">{t('profile.userId')}</label>
                    <p className="profile__info-value">{user.id}</p>
                  </div>

                  <div className="profile__info-item">
                    <label className="profile__info-label">{t('profile.role')}</label>
                    <p className="profile__info-value">{t(`profile.role${user.role.charAt(0).toUpperCase() + user.role.slice(1)}`)}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default Profile; 